<script setup lang="ts">
import VExpansionPanel from '@/shared/ui/VExpansionPanel.vue';
import { useGoogleMap } from '@/features/googleMap';
import { useClipboard } from '@/shared/model/useClipboard';
import type { InfoItem } from '../lib/types';

export type Props = {
    title: string;
    items: InfoItem[];
    defaultOpen?: boolean;
    disabled?: boolean;
    variant?: 'default' | 'filled' | 'outlined';
};

withDefaults(defineProps<Props>(), {
    defaultOpen: false,
    disabled: false,
    variant: 'default',
});

const { openGoogleMap } = useGoogleMap();
const { showSnackbar: showCopySnackbar, copyToClipboard: handleCopy } = useClipboard();

const handleMapClick = (item: InfoItem) => {
    if (item.latitude && item.longitude) {
        openGoogleMap({ latitude: item.latitude, longitude: item.longitude });
    }
};
</script>

<template>
    <VExpansionPanel :title="title" :default-open="defaultOpen" :disabled="disabled" :variant="variant">
        <div class="info-panel-content">
            <div class="info-panel-grid">
                <div v-for="(item, index) in items" :key="index" class="info-panel-row full-width">
                    <div class="info-panel-label">{{ item.label }}</div>
                    <div class="info-panel-value" :class="{ 'with-icons': item.showIcons }">
                        {{ item.value }}
                        <div v-if="item.showIcons" class="info-panel-icons">
                            <img
                                src="@/assets/icons/icon-clipboard.png"
                                alt="住所をコピー"
                                class="icon-button"
                                @click="handleCopy(item.value)"
                            />
                            <img
                                v-if="item.latitude && item.longitude"
                                src="@/assets/icons/icon-pin.png"
                                alt="Google Mapで開く"
                                class="icon-button"
                                @click="handleMapClick(item)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <v-snackbar v-model="showCopySnackbar" :timeout="2000" location="top"> コピーしました </v-snackbar>
    </VExpansionPanel>
</template>

<style scoped>
.info-panel-content {
    padding: 16px;
    background-color: #fafafa;
}

.info-panel-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
}

.info-panel-row {
    display: flex;
    align-items: flex-start;
    padding: 12px 0;
    border-bottom: 1px solid #e0e0e0;
}

.info-panel-row.full-width {
    grid-column: 1 / -1;
}

.info-panel-row:last-child {
    border-bottom: none;
}

.info-panel-label {
    flex: 0 0 140px;
    font-size: 14px;
    color: #333;
    font-weight: 500;
    padding-right: 16px;
}

.info-panel-value {
    flex: 1;
    font-size: 14px;
    color: #333;
    line-height: 1.4;
}

.info-panel-value.with-icons {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.info-panel-icons {
    display: flex;
    gap: 8px;
    margin-left: 16px;
}

.icon-button {
    width: 24px;
    height: 24px;
    cursor: pointer;
    transition: opacity 0.2s;
}

.icon-button:hover {
    opacity: 0.7;
}

@media (max-width: 768px) {
    .info-panel-grid {
        grid-template-columns: 1fr;
    }

    .info-panel-row {
        flex-direction: column;
        align-items: stretch;
    }

    .info-panel-label {
        flex: none;
        padding-right: 0;
        padding-bottom: 4px;
        font-size: 13px;
    }

    .info-panel-value.with-icons {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .info-panel-icons {
        margin-left: 0;
    }
}
</style>
